/**
 * Which planned lessons are selected — rows ticked one by one, or every lesson the filter
 * matches less the ones unticked since. The second is never a list of ids: it goes to the
 * API as the filter, so selecting 2,443 lessons costs nothing to hold or send.
 */
export type Selection =
  | { readonly all: false; readonly ids: ReadonlySet<string> }
  | { readonly all: true; readonly except: ReadonlySet<string> };

export const NONE: Selection = { all: false, ids: new Set() };

export const ALL: Selection = { all: true, except: new Set() };

/** `total` is every match of the current filter. */
export const countOf = (selection: Selection, total: number): number =>
  selection.all
    ? Math.max(0, total - selection.except.size)
    : selection.ids.size;

export const isPicked = (selection: Selection, id: string): boolean =>
  selection.all ? !selection.except.has(id) : selection.ids.has(id);

const flip = (
  set: ReadonlySet<string>,
  ids: readonly string[],
  on: boolean,
) => {
  const next = new Set(set);
  for (const id of ids) {
    if (on) next.add(id);
    else next.delete(id);
  }
  return next;
};

export function pick(
  selection: Selection,
  ids: readonly string[],
  on: boolean,
): Selection {
  return selection.all
    ? { all: true, except: flip(selection.except, ids, !on) }
    : { all: false, ids: flip(selection.ids, ids, on) };
}

export const toggle = (selection: Selection, id: string): Selection =>
  pick(selection, [id], !isPicked(selection, id));

/** What the export's `selected` mode needs beside the filter. */
export const exportScope = (
  selection: Selection,
): { ids?: string; exclude?: string } => {
  if (!selection.all) return { ids: [...selection.ids].join(',') };
  return selection.except.size === 0
    ? {}
    : { exclude: [...selection.except].join(',') };
};
